/**
 * V2 visitor ribbon — the signed-out answer to "what is this and why
 * would I join?" (2026-07-26, the landing-pages push).
 *
 * Most first visits are not the splash page: they are a shared link to
 * a profile, a category, a list, a place or a single yoink. Those
 * pages lead with the sharer's content — as they should — which left a
 * first-time visitor looking at a stack of photos with no idea what
 * Yoink is or how to start. The ribbon is one fixed line at the bottom
 * of every shared-link arrival: what Yoink is (in the page's own
 * context), a Join button, and How it works.
 *
 * It lives in the app chrome, OUTSIDE #v2-app, because views repaint
 * their whole root with innerHTML — anything mounted inside dies on the
 * next render. main.js mounts it once and calls setRoute() on every
 * navigation; views that know whose page this is sharpen the line with
 * setContext("Like Dru's Airports? …"), which setRoute clears so a
 * stale name can never outlive its page.
 */
import { escapeHtml } from '../../assets/js/utils.js';
import { OPEN_AUTH_EVENT } from './auth-button.js';
import { track } from '../core/analytics.js';

/**
 * Shared-link and search arrivals only. The signed-in surfaces (log,
 * account, my-lists, admin) handle their own signed-out states, and a
 * not-found page has nothing to sell.
 */
const SHOW_ON = new Set([
  'discover', 'location', 'category', 'categories', 'yoink', 'list',
  'user', 'user-geography', 'user-trophies', 'user-awards',
  'user-collections', 'user-categories', 'user-lists',
]);

/** The line each arrival opens with until its view knows names. */
const DEFAULT_LINES = {
  discover: 'Collect the places you visit — photos, pins on a map, and collections to complete.',
  location: 'Been here? Log it with a photo — that’s a yoink.',
  category: 'How many of these have you been to? Make them count.',
  categories: 'Pick a category and start checking places off.',
  yoink: 'One place, logged with a photo — that’s a yoink. Collect your own.',
  list: 'Save the places worth going — and make lists of your own.',
  user: 'Explorers keep their travels here — photos, pins and collections. Start yours.',
};

let elRef = null;
let storeRef = null;
let route = null;
let context = null;
let lastHtml = null;

function line() {
  if (context) return context;
  // Every /user/... showcase page is a window into one explorer's journal
  return DEFAULT_LINES[route] || (route?.startsWith('user-') ? DEFAULT_LINES.user : '');
}

function render() {
  if (!elRef) return;
  const { authReady, user } = storeRef.get();
  const text = line();
  const show = authReady && !user && SHOW_ON.has(route) && text;
  const html = show
    ? `
      <div class="v2-visitor-ribbon" role="complementary" aria-label="About Yoink Adventures">
        <p class="ribbon-line">${escapeHtml(text)}</p>
        <div class="ribbon-actions">
          <button type="button" class="ribbon-join" data-ribbon-join>Join free</button>
          <a class="ribbon-how" href="https://yoinkadventures.com/how-it-works/"
            target="_blank" rel="noopener" data-ribbon-how>How it works</a>
        </div>
      </div>`
    : '';
  if (html === lastHtml) return; // auth/route churn without a visible change
  lastHtml = html;
  elRef.innerHTML = html;
  document.documentElement.classList.toggle('v2-ribbon-on', !!show);
  publishHeight();
}

/**
 * Publish the ribbon's real height to CSS as --yoink-ribbon-h.
 *
 * The site-wide GTranslate flag row (functions.php / v2/adventure/index.php)
 * is fixed at bottom-left with z-index 8500 — thousands above the ribbon's
 * 90 — so it drew straight over the Join button (2026-07-27 device report:
 * "I keep trying to move the WP flags out of the way but the new band for
 * people not logged in is getting stepped on"). Fighting that with z-index
 * would only bury the flags instead. Measuring is the honest fix: the bar
 * wraps to one line or two depending on width and the sharpened context
 * line, so its height cannot be a constant.
 */
function publishHeight() {
  const root = document.documentElement;
  const bar = elRef?.querySelector('.v2-visitor-ribbon');
  if (!bar) {
    root.style.removeProperty('--yoink-ribbon-h');
    return;
  }
  // 0 in jsdom (no layout) — leave the CSS fallback in charge rather than
  // publishing a height that would pin the flags to the bottom edge
  const h = Math.round(bar.getBoundingClientRect().height);
  if (h > 0) root.style.setProperty('--yoink-ribbon-h', `${h}px`);
}

/** main.js, on every navigation — resets any page-specific line. */
export function setRoute(routeName) {
  route = routeName;
  context = null;
  render();
}

/**
 * A view that knows whose page this is calls in with a sharper line
 * ("Like Dru’s Airports? Start your own."). Plain text —
 * the ribbon escapes it. Safe to call when the ribbon isn't mounted
 * (embeds, tests): it just no-ops.
 */
export function setContext(text) {
  context = text || null;
  render();
}

export function mount(el, ctx) {
  elRef = el;
  storeRef = ctx.store;
  lastHtml = null;

  function onClick(event) {
    if (event.target.closest('[data-ribbon-join]')) {
      track('visitor_cta', { action: 'join', view: route });
      // The header owns the auth dialog; ask for it by event, opened on
      // the Create-account tab — this button says "Join", not "Sign in"
      document.dispatchEvent(new CustomEvent(OPEN_AUTH_EVENT, { detail: { mode: 'signup' } }));
      return;
    }
    if (event.target.closest('[data-ribbon-how]')) {
      track('visitor_cta', { action: 'how-it-works', view: route }); // link proceeds
    }
  }

  // Rotating the phone re-wraps the bar, so the height it published is
  // stale the moment the orientation changes
  function onResize() { publishHeight(); }

  // …and so does a late web font. Measuring only on render/resize left the
  // published height 10px short once Caveat and Source Sans swapped in and
  // the line re-wrapped — the flags crept back down to a 2px gap. A
  // ResizeObserver on the host tracks the bar's real height whatever moves
  // it. Guarded: jsdom has no ResizeObserver, and the CSS fallback covers
  // that case.
  const observer = typeof ResizeObserver === 'function'
    ? new ResizeObserver(() => publishHeight())
    : null;
  observer?.observe(el);

  el.addEventListener('click', onClick);
  window.addEventListener('resize', onResize);
  window.addEventListener('orientationchange', onResize);
  const unsubscribe = storeRef.subscribe(render);
  render();

  return () => {
    observer?.disconnect();
    el.removeEventListener('click', onClick);
    window.removeEventListener('resize', onResize);
    window.removeEventListener('orientationchange', onResize);
    unsubscribe();
    document.documentElement.classList.remove('v2-ribbon-on');
    document.documentElement.style.removeProperty('--yoink-ribbon-h');
    elRef = null;
    storeRef = null;
    route = null;
    context = null;
  };
}
